import { Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { FC } from 'react';
import { CustomerSegmentWithRelations } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import FormDialog from '../Dialogs/FormDialog';
import ReactHookFormTextField from '../ReactHookForm/ReactHookFormTextField';
import { Forecaster } from '../../types/Forecaster.interface';
import ReactHookFormAutoComplete from '../ReactHookForm/ReactHookFormAutoComplete';
import { Worktype } from '../../types/Worktype.interface';

interface WorktypeDialogProps {
    worktype?: Worktype;
    showDialog: boolean;
    forecasters: Forecaster[];
    customerSegments: CustomerSegmentWithRelations[];
    onSubmit: any;
    isLoading: boolean;
    onClose: any;
}

const WorktypeDialog: FC<WorktypeDialogProps> = ({
    worktype,
    showDialog,
    forecasters,
    customerSegments,
    onSubmit,
    isLoading,
    onClose,
}) => {
    const { t } = useTranslation();
    const form = useForm({
        defaultValues: {
            id: worktype?.id || '',
            worktype: worktype?.worktype || '',
            shortName: worktype?.shortName || '',
            forecasters: worktype?.forecasters || [],
            forecasterIds: (worktype?.forecasters || []).map((forecaster) => forecaster.id),
            customerSegments: worktype?.customerSegments || [],
            customerSegmentIds: (worktype?.customerSegments || []).map((segment) => segment.id),
        },
    });
    const {
        formState: { errors },
        control,
    } = form;

    const handleClose = () => {
        onClose();
    };

    return (
        <FormDialog
            title={worktype?.id ? t('worktype.editTitle') : t('worktype.createTitle')}
            showDialog={showDialog}
            form={form}
            onSubmit={onSubmit}
            onClose={handleClose}
            isLoading={isLoading}
            id="worktype-dialog"
        >
            <Grid item xs={12}>
                <ReactHookFormTextField
                    name="worktype"
                    label={t('field.name')}
                    control={control}
                    rules={{ required: { value: true, message: t('validation.required') } }}
                    formErrors={errors}
                    isNumeric={false}
                    defaultValue=""
                    inputProps={undefined}
                    isMultiline={false}
                ></ReactHookFormTextField>
            </Grid>

            <Grid item xs={12}>
                <ReactHookFormTextField
                    name="shortName"
                    label={t('field.shortName')}
                    control={control}
                    rules={{ maxLength: { value: 23, message: t('validation.maxChars', { count: 23 }) } }}
                    formErrors={errors}
                    isNumeric={false}
                    defaultValue=""
                    inputProps={{ maxLength: 23 }}
                    isMultiline={false}
                ></ReactHookFormTextField>
            </Grid>

            <Grid item xs={12}>
                <ReactHookFormAutoComplete
                    name="forecasters"
                    label={t('field.forecasters')}
                    control={control}
                    formErrors={errors}
                    defaultValue={undefined}
                    options={forecasters}
                    labelKeys={['name']}
                    multiple={true}
                    onChange={(event: any, values: Forecaster[] | undefined) => {
                        form.setValue('forecasterIds', values ? values.map((f) => f.id) : []);
                    }}
                />
            </Grid>

            <Grid item xs={12}>
                <ReactHookFormAutoComplete
                    name="customerSegments"
                    label={t('segment.label')}
                    control={control}
                    formErrors={errors}
                    defaultValue={undefined}
                    options={customerSegments}
                    labelKeys={['label']}
                    multiple={true}
                    rules={{
                        validate: (value: CustomerSegmentWithRelations[] | undefined) =>
                            !value || value.length <= 2 || t('validation.maxSegments'),
                    }}
                    onChange={(event: any, values: CustomerSegmentWithRelations[] | undefined) => {
                        form.setValue('customerSegmentIds', values ? values.map((s) => s.id) : []);
                    }}
                />
            </Grid>
        </FormDialog>
    );
};

export default WorktypeDialog;
